/* ==========================================================
   BARROS THEME — GLOBAL JAVASCRIPT
   ========================================================== */

(() => {
  'use strict';

  const BarrosTheme = {
    instances: new WeakMap(),

    register(element, instance) {
      if (!element || !instance) return;

      this.instances.set(element, instance);
      element.dataset.barrosInitialized = 'true';
    },

    getInstance(element) {
      return this.instances.get(element);
    },

    destroyWithin(scope) {
      if (!scope) return;

      const initializedElements = scope.querySelectorAll(
        '[data-barros-initialized="true"]'
      );

      initializedElements.forEach((element) => {
        const instance = this.getInstance(element);

        if (
          instance &&
          typeof instance.destroy === 'function'
        ) {
          instance.destroy();
        }

        delete element.dataset.barrosInitialized;
        this.instances.delete(element);
      });
    }
  };

  /* ========================================================
     UTILITIES
     ======================================================== */

  class BarrosReveal {
    constructor(element, options = {}) {
      this.element = element;

      this.options = {
        threshold: 0.18,
        visibleClass: 'is-visible',
        once: true,
        ...options
      };

      this.observer = null;

      this.handleIntersection =
        this.handleIntersection.bind(this);

      this.init();
    }

    init() {
      if (
        window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches
      ) {
        this.show();
        return;
      }

      if (!('IntersectionObserver' in window)) {
        this.show();
        return;
      }

      this.observer = new IntersectionObserver(
        this.handleIntersection,
        {
          threshold: this.options.threshold
        }
      );

      this.observer.observe(this.element);
    }

    handleIntersection(entries) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        this.show();

        if (
          this.options.once &&
          this.observer
        ) {
          this.observer.unobserve(
            this.element
          );
        }
      });
    }

    show() {
      this.element.classList.add(
        this.options.visibleClass
      );
    }

    destroy() {
      if (this.observer) {
        this.observer.disconnect();
      }
    }
  }

  /* ========================================================
     OFFICINA BARROS
     ======================================================== */

  class BarrosWorkshop {
    constructor(element) {
      this.element = element;

      this.reveal = new BarrosReveal(
        element,
        {
          threshold: 0.18,
          visibleClass: 'is-visible',
          once: true
        }
      );
    }

    destroy() {
      if (this.reveal) {
        this.reveal.destroy();
      }
    }
  }

  /* ========================================================
     BRANDS SHOWCASE
     ======================================================== */

  class BarrosBrandsShowcase {
    constructor(root) {
      this.root = root;

      this.triggers = Array.from(
        root.querySelectorAll(
          '[data-barros-brand-trigger]'
        )
      );

      this.scenes = Array.from(
        root.querySelectorAll(
          '[data-barros-brand-scene]'
        )
      );

      this.activeIndex = 0;
      this.hoverTimer = null;

      this.desktopMedia = window.matchMedia(
        '(min-width: 750px)'
      );

      this.handleKeydown =
        this.handleKeydown.bind(this);

      this.handleResize =
        this.handleResize.bind(this);

      this.init();
    }

    init() {
      if (
        !this.triggers.length ||
        !this.scenes.length
      ) {
        return;
      }

      this.triggers.forEach(
        (trigger, index) => {
          trigger.addEventListener(
            'mouseenter',
            () => {
              if (
                !this.desktopMedia.matches
              ) {
                return;
              }

              this.scheduleChange(index);
            }
          );

          trigger.addEventListener(
            'focus',
            () => {
              this.setActive(index);
            }
          );

          trigger.addEventListener(
            'click',
            () => {
              this.setActive(index);
            }
          );
        }
      );

      this.root.addEventListener(
        'keydown',
        this.handleKeydown
      );

      window.addEventListener(
        'resize',
        this.handleResize
      );

      this.setActive(0, false);
    }

    scheduleChange(index) {
      window.clearTimeout(
        this.hoverTimer
      );

      this.hoverTimer =
        window.setTimeout(() => {
          this.setActive(index);
        }, 90);
    }

    setActive(index, animate = true) {
      if (
        index < 0 ||
        index >= this.triggers.length ||
        index >= this.scenes.length
      ) {
        return;
      }

      this.activeIndex = index;

      this.triggers.forEach(
        (trigger, triggerIndex) => {
          const isActive =
            triggerIndex === index;

          trigger.classList.toggle(
            'is-active',
            isActive
          );

          trigger.removeAttribute('aria-selected');
          if (isActive) trigger.setAttribute('aria-current', 'true');
          else trigger.removeAttribute('aria-current');
        }
      );

      this.scenes.forEach(
        (scene, sceneIndex) => {
          const isActive =
            sceneIndex === index;

          scene.classList.toggle(
            'is-active',
            isActive
          );

          scene.setAttribute(
            'aria-hidden',
            isActive ? 'false' : 'true'
          );
          scene.inert = !isActive;

          if (!animate && isActive) {
            scene.style.transition = 'none';

            window.requestAnimationFrame(
              () => {
                scene.style.removeProperty(
                  'transition'
                );
              }
            );
          }
        }
      );
    }

    handleKeydown(event) {
      if (!this.triggers.length) return;

      if (
        event.key === 'ArrowDown' ||
        event.key === 'ArrowRight'
      ) {
        event.preventDefault();
        this.moveFocus(1);
        return;
      }

      if (
        event.key === 'ArrowUp' ||
        event.key === 'ArrowLeft'
      ) {
        event.preventDefault();
        this.moveFocus(-1);
        return;
      }

      if (event.key === 'Home') {
        event.preventDefault();
        this.focusTrigger(0);
        return;
      }

      if (event.key === 'End') {
        event.preventDefault();

        this.focusTrigger(
          this.triggers.length - 1
        );
      }
    }

    moveFocus(direction) {
      const focusedIndex =
        this.triggers.findIndex(
          (trigger) =>
            trigger ===
            document.activeElement
        );

      const startIndex =
        focusedIndex >= 0
          ? focusedIndex
          : this.activeIndex;

      const nextIndex =
        (
          startIndex +
          direction +
          this.triggers.length
        ) % this.triggers.length;

      this.focusTrigger(nextIndex);
    }

    focusTrigger(index) {
      const trigger =
        this.triggers[index];

      if (!trigger) return;

      this.setActive(index);
      trigger.focus();
    }

    handleResize() {
      window.clearTimeout(
        this.hoverTimer
      );
    }

    destroy() {
      this.root.removeEventListener(
        'keydown',
        this.handleKeydown
      );

      window.removeEventListener(
        'resize',
        this.handleResize
      );

      window.clearTimeout(
        this.hoverTimer
      );
    }
  }

  /* ========================================================
     MEGA MENU
     ======================================================== */

  class BarrosMegaMenu {
    constructor(root) {
      this.root = root;

      this.headerShell = root.closest(
        '.barros-header-shell'
      );

      this.trigger = document.querySelector(
        `[aria-controls="${root.id}"]`
      );

      this.closeButton = root.querySelector(
        '[data-barros-mega-close]'
      );

      this.backdrop = root.querySelector(
        '[data-barros-mega-backdrop]'
      );

      this.categories = Array.from(
        root.querySelectorAll(
          '[data-barros-mega-item]'
        )
      );

      this.categoryTriggers =
        this.categories.map(
          (category) =>
            category.querySelector(
              '[data-barros-mega-category-trigger]'
            )
        );

      this.scenes = Array.from(
        root.querySelectorAll(
          '[data-barros-mega-scene]'
        )
      );

      this.progressItems = Array.from(
        root.querySelectorAll(
          '[data-barros-mega-progress]'
        )
      );

      this.subnavs = Array.from(
        root.querySelectorAll(
          '[data-barros-mega-subnav]'
        )
      );

      this.defaultIndex =
        Number.parseInt(
          root.dataset.defaultIndex || '0',
          10
        );

      this.activeIndex =
        Number.isNaN(this.defaultIndex)
          ? 0
          : this.defaultIndex;

      this.isOpen = false;
      this.hoverTimer = null;
      this.lastFocusedElement = null;

      this.handleTriggerClick =
        this.handleTriggerClick.bind(this);

      this.handleBackdropClick =
        this.handleBackdropClick.bind(this);

      this.handleDocumentClick =
        this.handleDocumentClick.bind(this);

      this.handleKeydown =
        this.handleKeydown.bind(this);

      this.handleResize =
        this.handleResize.bind(this);

      this.init();
    }

    init() {
      if (
        !this.root ||
        !this.trigger
      ) {
        return;
      }

      this.trigger.addEventListener(
        'click',
        this.handleTriggerClick
      );

      if (this.closeButton) {
        this.closeButton.addEventListener(
          'click',
          () => this.close(true)
        );
      }

      if (this.backdrop) {
        this.backdrop.addEventListener(
          'click',
          this.handleBackdropClick
        );
      }

      this.categories.forEach(
        (category, index) => {
          const categoryTrigger =
            this.categoryTriggers[index];

          category.addEventListener(
            'mouseenter',
            () => {
              this.scheduleCategoryChange(
                index
              );
            }
          );

          category.addEventListener(
            'focusin',
            () => {
              this.setActive(index);
            }
          );

          if (categoryTrigger) {
            categoryTrigger.addEventListener(
              'click',
              () => {
                this.setActive(index);
              }
            );
          }
        }
      );

      document.addEventListener(
        'click',
        this.handleDocumentClick
      );

      document.addEventListener(
        'keydown',
        this.handleKeydown
      );

      window.addEventListener(
        'resize',
        this.handleResize
      );

      this.root.inert = true;
      this.setActive(
        this.activeIndex,
        false
      );
    }

    handleTriggerClick(event) {
      event.preventDefault();

      if (this.isOpen) {
        this.close(false);
      } else {
        this.open();
      }
    }

    handleBackdropClick() {
      this.close(true);
    }

    handleDocumentClick(event) {
      if (!this.isOpen) return;

      const clickedInsideMenu =
        this.root.contains(event.target);

      const clickedTrigger =
        this.trigger.contains(event.target);

      if (
        !clickedInsideMenu &&
        !clickedTrigger
      ) {
        this.close(false);
      }
    }

    handleKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.moveCategoryFocus(1);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.moveCategoryFocus(-1);
        return;
      }

      if (event.key === 'Home') {
        event.preventDefault();
        this.focusCategory(0);
        return;
      }

      if (event.key === 'End') {
        event.preventDefault();

        this.focusCategory(
          this.categories.length - 1
        );
      }
    }

    handleResize() {
      if (
        window.innerWidth <= 989 &&
        this.isOpen
      ) {
        this.close(false);
      }
    }

    open() {
      if (window.innerWidth <= 989) {
        return;
      }

      this.lastFocusedElement =
        document.activeElement;

      this.isOpen = true;

      this.root.inert = false;
      this.root.classList.add(
        'is-open'
      );

      this.root.setAttribute(
        'aria-hidden',
        'false'
      );

      this.trigger.classList.add(
        'is-open'
      );

      this.trigger.setAttribute(
        'aria-expanded',
        'true'
      );

      if (this.headerShell) {
        this.headerShell.classList.add(
          'has-open-mega'
        );
      }

      document.body.classList.add(
        'barros-mega-open'
      );

      this.setActive(
        this.activeIndex,
        false
      );
    }

    close(restoreFocus = false) {
      this.isOpen = false;

      window.clearTimeout(
        this.hoverTimer
      );

      if (this.root.contains(document.activeElement) && this.trigger) this.trigger.focus();
      this.root.classList.remove(
        'is-open'
      );

      this.root.setAttribute(
        'aria-hidden',
        'true'
      );
      this.root.inert = true;

      this.trigger.classList.remove(
        'is-open'
      );

      this.trigger.setAttribute(
        'aria-expanded',
        'false'
      );

      if (this.headerShell) {
        this.headerShell.classList.remove(
          'has-open-mega'
        );
      }

      document.body.classList.remove(
        'barros-mega-open'
      );

      if (
        restoreFocus &&
        this.lastFocusedElement &&
        typeof this.lastFocusedElement
          .focus === 'function'
      ) {
        this.lastFocusedElement.focus();
      }
    }

    scheduleCategoryChange(index) {
      window.clearTimeout(
        this.hoverTimer
      );

      this.hoverTimer =
        window.setTimeout(() => {
          this.setActive(index);
        }, 90);
    }

    setActive(index, animate = true) {
      if (
        index < 0 ||
        index >= this.categories.length
      ) {
        return;
      }

      this.activeIndex = index;

      this.categories.forEach(
        (category, categoryIndex) => {
          const isActive =
            categoryIndex === index;

          category.classList.toggle(
            'is-active',
            isActive
          );

          const categoryTrigger =
            this.categoryTriggers[
              categoryIndex
            ];

          if (categoryTrigger) {
            categoryTrigger.setAttribute(
              'aria-expanded',
              isActive ? 'true' : 'false'
            );
          }
        }
      );

      this.scenes.forEach(
        (scene, sceneIndex) => {
          const isActive =
            sceneIndex === index;

          scene.classList.toggle(
            'is-active',
            isActive
          );

          scene.setAttribute(
            'aria-hidden',
            isActive ? 'false' : 'true'
          );

          if (!animate && isActive) {
            scene.style.transition = 'none';

            window.requestAnimationFrame(
              () => {
                scene.style.removeProperty(
                  'transition'
                );
              }
            );
          }
        }
      );

      this.progressItems.forEach(
        (item, progressIndex) => {
          item.classList.toggle(
            'is-active',
            progressIndex === index
          );
        }
      );

      this.subnavs.forEach(
        (subnav, subnavIndex) => {
          const isActive =
            subnavIndex === index;

          subnav.setAttribute(
            'aria-hidden',
            isActive ? 'false' : 'true'
          );
          subnav.inert = !isActive;
        }
      );
    }

    moveCategoryFocus(direction) {
      if (!this.categories.length) {
        return;
      }

      const focusedIndex =
        this.categoryTriggers.findIndex(
          (trigger) =>
            trigger ===
            document.activeElement
        );

      const startIndex =
        focusedIndex >= 0
          ? focusedIndex
          : this.activeIndex;

      const nextIndex =
        (
          startIndex +
          direction +
          this.categories.length
        ) % this.categories.length;

      this.focusCategory(nextIndex);
    }

    focusCategory(index) {
      const trigger =
        this.categoryTriggers[index];

      if (!trigger) return;

      this.setActive(index);
      trigger.focus();
    }

    destroy() {
      this.trigger.removeEventListener(
        'click',
        this.handleTriggerClick
      );

      if (this.backdrop) {
        this.backdrop.removeEventListener(
          'click',
          this.handleBackdropClick
        );
      }

      document.removeEventListener(
        'click',
        this.handleDocumentClick
      );

      document.removeEventListener(
        'keydown',
        this.handleKeydown
      );

      window.removeEventListener(
        'resize',
        this.handleResize
      );

      window.clearTimeout(
        this.hoverTimer
      );
    }
  }

  /* ========================================================
     INITIALIZATION
     ======================================================== */

  function initializeWithin(
    scope = document
  ) {
    scope
      .querySelectorAll(
        '[data-barros-workshop]'
      )
      .forEach((element) => {
        if (
          element.dataset
            .barrosInitialized === 'true'
        ) {
          return;
        }

        BarrosTheme.register(
          element,
          new BarrosWorkshop(element)
        );
      });

    scope
      .querySelectorAll(
        '[data-barros-brands]'
      )
      .forEach((element) => {
        if (
          element.dataset
            .barrosInitialized === 'true'
        ) {
          return;
        }

        BarrosTheme.register(
          element,
          new BarrosBrandsShowcase(
            element
          )
        );
      });

    scope
      .querySelectorAll(
        '[data-barros-mega]'
      )
      .forEach((element) => {
        if (
          element.dataset
            .barrosInitialized === 'true'
        ) {
          return;
        }

        BarrosTheme.register(
          element,
          new BarrosMegaMenu(element)
        );
      });
  }

  function initializeDocument() {
    initializeWithin(document);
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initializeDocument,
      { once: true }
    );
  } else {
    initializeDocument();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeWithin(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      BarrosTheme.destroyWithin(
        event.target
      );
    }
  );

  window.BarrosTheme = BarrosTheme;
})();